import {
  BULK_DECISION_MAX,
  CANDIDATE_STATUS_LABELS_INTERNAL,
  fmtUtcDayTime,
  regionLabel,
} from '@remote-medium/shared';
import {
  Button,
  Checkbox,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  cn,
  errorMessage,
  unwrap,
  useApi,
} from '@remote-medium/ui';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useBillingEnabled } from '../../../lib/billing.tsx';
import { admin } from '../../../lib/client.ts';
import {
  ConfirmButton,
  DataTable,
  EmptyRow,
  H2,
  Notice,
  StatusRow,
  Tabs,
  tableReady,
} from '../../../lib/ui.tsx';
import { CandidateSheet, type MatchNotes } from './CandidateSheet.tsx';
import { STAGES, regionFit, stageOf, standing, type MatchRow, type Stage } from './stage.ts';

const PAGE = 12;
const COLS = 7;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function SkillDots({ have, total }: { have: number; total: number }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-flex gap-0.5" aria-hidden="true">
        {Array.from({ length: Math.min(total, 6) }, (_, i) => (
          <span key={i} className={cn('size-2 rounded-xs', i < have ? 'bg-primary' : 'bg-muted')} />
        ))}
      </span>
      <span className="tabular-nums">
        {have} of {total}
      </span>
    </span>
  );
}

async function loadContext() {
  const weights = await unwrap(admin['ranking-config'].$get());
  return { minScore: weights.minScore };
}

// Ranked candidates for a company role, split by where each match stands.
// Curators see identity here — this is staff-only.
export function Candidates({
  roleId,
  roleStatus,
  roleRegions,
  company,
}: {
  roleId: string;
  roleStatus: string;
  roleRegions: string[];
  company: string;
}) {
  const billing = useBillingEnabled();
  const firstIntroCost = billing
    ? ' The first introduction on a role uses one credit.'
    : ' No credit is used while billing is off.';
  const { data, error, loading, reload, setData } = useApi(
    () => unwrap(admin.roles[':id'].matches.$get({ param: { id: roleId } })),
    [roleId],
  );
  const ctx = useApi(loadContext, [roleId]);
  const [tab, setTab] = useState<Stage | null>(null);
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sheet, setSheet] = useState<{ id: string; open: boolean } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);
  const closed = roleStatus === 'closed';

  const ready = tableReady({ data, loading, error });
  const matches = useMemo(
    () =>
      [...(data?.matches ?? [])]
        .sort((a, b) => b.systemScore - a.systemScore)
        .map((m) => ({ m, stage: stageOf(m) })),
    [data],
  );
  const counts = Object.fromEntries(
    STAGES.map((s) => [s.value, matches.filter((x) => x.stage === s.value).length]),
  ) as Record<Stage, number>;
  const current: Stage =
    tab ?? (counts.review ? 'review' : (STAGES.find((s) => counts[s.value])?.value ?? 'review'));
  const needle = q.trim().toLowerCase();
  const rows = matches.filter(
    (x) => x.stage === current && (!needle || x.m.fullName.toLowerCase().includes(needle)),
  );
  const shown = all ? rows : rows.slice(0, PAGE);
  const selectable = !closed && (current === 'review' || current === 'confirmed');
  const picked = rows.filter((x) => selected.has(x.m.id));
  const open = sheet && matches.find((x) => x.m.id === sheet.id);

  const changeTab = (t: Stage) => {
    setTab(t);
    setAll(false);
    setSelected(new Set());
  };

  async function run(name: string, fn: () => Promise<string>) {
    setBusy(name);
    setMsg(null);
    try {
      setMsg({ tone: 'info', text: await fn() });
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  }

  const replace = (updated: MatchRow | null) => {
    if (data && updated)
      setData({ ...data, matches: data.matches.map((x) => (x.id === updated.id ? updated : x)) });
  };

  // Sheet actions throw so the sheet shows the error.
  async function decide(m: MatchRow, decision: MatchRow['curatorDecision'], notes: MatchNotes) {
    replace(
      await unwrap(
        admin.matches[':id'].decision.$post({ param: { id: m.id }, json: { decision, ...notes } }),
      ),
    );
    if (decision !== m.curatorDecision) setSheet((s) => s && { ...s, open: false });
  }

  async function saveNotes(m: MatchRow, notes: MatchNotes) {
    replace(await unwrap(admin.matches[':id'].notes.$patch({ param: { id: m.id }, json: notes })));
  }

  const bulk = (decision: 'confirmed' | 'rejected') =>
    run('bulk', async () => {
      try {
        const ids = picked.map((x) => x.m.id);
        const r = { updated: 0, skipped: 0 };
        for (let i = 0; i < ids.length; i += BULK_DECISION_MAX) {
          const part = await unwrap(
            admin.roles[':id'].decisions.$post({
              param: { id: roleId },
              json: { decision, matchIds: ids.slice(i, i + BULK_DECISION_MAX) },
            }),
          );
          r.updated += part.updated;
          r.skipped += part.skipped;
        }
        return r.skipped
          ? `${r.updated} ${decision}. ${r.skipped} skipped: consent was already asked.`
          : `${r.updated} ${decision}.`;
      } finally {
        setSelected(new Set());
        reload();
      }
    });

  const rerank = () =>
    run('rerank', async () => {
      const r = await unwrap(admin.roles[':id'].rerank.$post({ param: { id: roleId } }));
      reload();
      return `Re-ranked: ${r.considered} eligible, ${r.written} at or above the minimum, ${r.belowMin} below.`;
    });

  const askConsent = () =>
    run('ask', async () => {
      const r = await unwrap(
        admin.roles[':id']['submit-shortlist'].$post({ param: { id: roleId } }),
      );
      reload();
      ctx.reload();
      return `Consent requests sent to ${r.asked} candidate${r.asked === 1 ? '' : 's'}.`;
    });

  // Throws so ConfirmButton keeps its dialog open and shows the error.
  const introduce = async (includeUnreacted: boolean) => {
    const r = await unwrap(
      admin.roles[':id']['send-introductions'].$post({
        param: { id: roleId },
        json: { includeUnreacted },
      }),
    );
    setMsg({
      tone: 'info',
      text: `Introduced ${r.introduced}. ${r.charged ? 'One role credit was used.' : billing ? 'No credit used (already paid, or nobody to introduce).' : 'No credit used: billing is off.'}`,
    });
    reload();
  };

  const lastRanked = data?.lastRankedAt ?? null;
  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className={H2}>Candidates</h2>
        <span className="text-xs text-muted-foreground">
          {[
            ready && `${matches.length} candidates above ${ctx.data?.minScore ?? 'the minimum'}`,
            lastRanked && `ranked ${fmtUtcDayTime(lastRanked)}`,
          ]
            .filter(Boolean)
            .map((t) => `${t} · `)}
          <button
            type="button"
            disabled={busy !== null || closed}
            onClick={() => void rerank()}
            className="text-foreground underline underline-offset-4 disabled:opacity-50"
          >
            {busy === 'rerank' ? 'Ranking…' : 'Re-rank now'}
          </button>
        </span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          label="Candidate stage"
          tabs={STAGES.map((s) => ({ ...s, count: ready ? counts[s.value] : undefined }))}
          value={current}
          onChange={changeTab}
        />
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          {current === 'confirmed' && counts.confirmed > 0 && (
            <Button size="sm" disabled={busy !== null || closed} onClick={() => void askConsent()}>
              {busy === 'ask' ? 'Sending…' : `Ask ${counts.confirmed} for consent`}
            </Button>
          )}
          {current === 'ready' && counts.ready > 0 && (
            <>
              <ConfirmButton
                disabled={busy !== null || closed}
                title="Introduce every consenting candidate?"
                description={`This includes candidates the company hasn't reacted to yet.${firstIntroCost}`}
                confirmLabel="Introduce all"
                onConfirm={() => introduce(true)}
              >
                Introduce all
              </ConfirmButton>
              <ConfirmButton
                variant="default"
                disabled={busy !== null || closed}
                title="Introduce the interested candidates?"
                description={`Candidates who said yes and the company marked interested get an introduction email with the company.${firstIntroCost}`}
                confirmLabel="Send introductions"
                onConfirm={() => introduce(false)}
              >
                Introduce interested
              </ConfirmButton>
            </>
          )}
          <InputGroup className="w-full sm:w-56">
            <InputGroupAddon>
              <Search aria-hidden="true" />
            </InputGroupAddon>
            <InputGroupInput
              type="search"
              placeholder="Find a candidate"
              aria-label="Find a candidate by name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </InputGroup>
        </div>
      </div>

      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}

      <div className="flex flex-col rounded-xl border border-border bg-card shadow-sm">
        <DataTable framed={false} minWidth="md:min-w-3xl">
          <thead>
            <tr>
              <th className="w-10">
                {selectable && rows.length > 0 ? (
                  <Checkbox
                    aria-label="Select all"
                    checked={picked.length === rows.length}
                    indeterminate={picked.length > 0 && picked.length < rows.length}
                    onCheckedChange={(on) =>
                      setSelected(new Set(on ? rows.map((x) => x.m.id) : []))
                    }
                  />
                ) : (
                  <span className="sr-only">Select</span>
                )}
              </th>
              <th className="w-20">Score</th>
              <th>Candidate</th>
              <th className="hidden md:table-cell">Required skills</th>
              <th className="hidden md:table-cell">Region · overlap</th>
              <th>Where it stands</th>
              <th className="w-10">
                <span className="sr-only">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <StatusRow colSpan={COLS} loading={loading} error={error} />
            {ready && rows.length === 0 && (
              <EmptyRow colSpan={COLS}>
                {needle
                  ? 'Nobody here matches that name.'
                  : matches.length === 0
                    ? 'No candidates above the minimum score yet. The pool is re-ranked nightly and whenever a profile is screened.'
                    : 'Nobody at this stage.'}
              </EmptyRow>
            )}
            {shown.map(({ m, stage }) => {
              const f = m.facts;
              const seniority = f?.candidateSeniority;
              return (
                <tr
                  key={m.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSheet({ id: m.id, open: true })}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    {selectable && (
                      <Checkbox
                        aria-label={`Select ${m.fullName}`}
                        checked={selected.has(m.id)}
                        onCheckedChange={() => toggle(m.id)}
                      />
                    )}
                  </td>
                  <td>
                    <div className="flex w-9 flex-col gap-1">
                      <span className="font-semibold tabular-nums">{m.systemScore}</span>
                      <span className="h-0.5 rounded-full bg-muted" aria-hidden="true">
                        <span
                          className="block h-full rounded-full bg-primary"
                          style={{ width: `${Math.min(100, m.systemScore)}%` }}
                        />
                      </span>
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="block text-left font-semibold text-foreground hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSheet({ id: m.id, open: true });
                      }}
                    >
                      {m.fullName}
                    </button>
                    <span className="text-xs text-muted-foreground">
                      {[
                        regionLabel(m.country),
                        seniority && cap(seniority),
                        m.candidateStatus !== 'active' &&
                          `${CANDIDATE_STATUS_LABELS_INTERNAL[m.candidateStatus]} (won't be asked or introduced)`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </td>
                  <td className="hidden whitespace-nowrap md:table-cell">
                    {f && f.requiredTotal > 0 ? (
                      <SkillDots have={f.requiredHave} total={f.requiredTotal} />
                    ) : (
                      <span className="text-muted-foreground">{f ? 'None listed' : '–'}</span>
                    )}
                  </td>
                  <td className="hidden whitespace-nowrap md:table-cell">
                    {regionFit(m, roleRegions)}
                    {f?.overlapHours != null && ` · ${Math.round(f.overlapHours)}h`}
                  </td>
                  <td className="text-muted-foreground">{standing(m, stage)}</td>
                  <td className="text-right text-muted-foreground">
                    <ChevronRight aria-hidden="true" className="ml-auto size-4" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </DataTable>

        {ready && rows.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3 text-xs text-muted-foreground">
            {picked.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-foreground">{picked.length} selected</span>
                {current === 'review' && (
                  <Button size="sm" disabled={busy !== null} onClick={() => void bulk('confirmed')}>
                    Confirm
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy !== null}
                  onClick={() => void bulk('rejected')}
                >
                  Reject
                </Button>
              </div>
            ) : (
              <span>
                Showing {shown.length} of {rows.length} · by score
              </span>
            )}
            {rows.length > PAGE && (
              <button
                type="button"
                aria-expanded={all}
                onClick={() => setAll(!all)}
                className="inline-flex items-center gap-1 text-sm font-medium text-foreground"
              >
                {all ? 'Show fewer' : `Show ${rows.length - PAGE} more`}
                <ChevronDown aria-hidden="true" className={cn('size-4', all && 'rotate-180')} />
              </button>
            )}
          </div>
        )}
      </div>

      <CandidateSheet
        open={!!sheet?.open}
        m={open?.m ?? null}
        stage={open?.stage ?? null}
        company={company}
        minScore={ctx.data?.minScore ?? null}
        closed={closed}
        onClose={() => setSheet((s) => s && { ...s, open: false })}
        onDecide={decide}
        onSaveNotes={saveNotes}
      />
    </section>
  );
}
